/**
 * Utility functions for working with location map images.
 */

/**
 * Finds location map image URL from slot selections.
 * 
 * @param {Array} slots - Array of slot selections to search through
 * @returns {string | null} The location map image URL (data URL or regular URL) or null if not found
 */
export function findLocationMapImageUrl(slots) {
  if (!slots || !Array.isArray(slots)) {
    return null;
  }

  // Search all slots for one that has location map data with an image URL.
  const locationMapSlot = slots.find((s) => {
    if (!s.selected) return false;
    
    let selectedData = s.selected;
    
    // Parse if it's a JSON string.
    if (typeof selectedData === 'string') {
      try {
        selectedData = JSON.parse(selectedData);
      } catch {
        return false;
      }
    }
    
    // Check if it's an object with locationMapImageUrl.
    if (selectedData && typeof selectedData === 'object' && selectedData !== null) {
      // Use bracket notation to safely access locationMapImageUrl.
      return 'locationMapImageUrl' in selectedData && selectedData['locationMapImageUrl'];
    }
    
    return false;
  });
  
  if (!locationMapSlot || !locationMapSlot.selected) {
    return null;
  }
  
  // Extract the locationMapImageUrl.
  let selectedData = locationMapSlot.selected;
  if (typeof selectedData === 'string') {
    try {
      selectedData = JSON.parse(selectedData);
    } catch {
      return null;
    }
  }
  
  // Use bracket notation to safely access locationMapImageUrl.
  if (selectedData && typeof selectedData === 'object' && selectedData !== null && 'locationMapImageUrl' in selectedData) {
    const imageUrl = selectedData['locationMapImageUrl'];
    return typeof imageUrl === 'string' ? imageUrl : null;
  }
  
  return null;
}

/**
 * Converts a data URL to a File object.
 * 
 * @param {string} dataUrl - The data URL string
 * @param {string} filename - The filename for the File (default: 'location-map.png')
 * @returns {File} The File object
 * @throws {Error} If the data URL is invalid
 */
export function dataUrlToFile(dataUrl, filename = 'location-map.png') {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('Invalid data URL provided');
  }
  
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Converts location map image URL to File object.
 * 
 * @param {string} locationMapImageUrl - The location map image URL (data URL or regular URL)
 * @param {string} filename - The filename for the File (default: 'location-map.png')
 * @returns {File | null} The File object or null if conversion fails
 */
export function locationMapImageUrlToFile(locationMapImageUrl, filename = 'location-map.png') {
  if (!locationMapImageUrl) {
    return null;
  }
  
  try {
    return dataUrlToFile(locationMapImageUrl, filename);
  } catch (error) {
    console.error('Failed to convert location map image to File:', error);
    return null;
  }
}

/**
 * Finds location map original image URL (non-inverted, for factory) from slot selections.
 * 
 * @param {Array} slots - Array of slot selections to search through
 * @returns {string | null} The original location map image URL or null if not found
 */
export function findLocationMapImageUrlOriginal(slots) {
  if (!slots || !Array.isArray(slots)) {
    return null;
  }

  const locationMapSlot = slots.find((s) => {
    if (!s.selected) return false;
    
    let selectedData = s.selected;
    if (typeof selectedData === 'string') {
      try {
        selectedData = JSON.parse(selectedData);
      } catch {
        return false;
      }
    }
    
    if (selectedData && typeof selectedData === 'object' && selectedData !== null) {
      return 'locationMapImageUrlOriginal' in selectedData && selectedData['locationMapImageUrlOriginal'];
    }
    
    return false;
  });
  
  if (!locationMapSlot || !locationMapSlot.selected) {
    return null;
  }
  
  let selectedData = locationMapSlot.selected;
  if (typeof selectedData === 'string') {
    try {
      selectedData = JSON.parse(selectedData);
    } catch {
      return null;
    }
  }
  
  if (selectedData && typeof selectedData === 'object' && selectedData !== null && 'locationMapImageUrlOriginal' in selectedData) {
    const imageUrl = selectedData['locationMapImageUrlOriginal'];
    return typeof imageUrl === 'string' ? imageUrl : null;
  }
  
  return null;
}

